// ─────────────────────────────────────────────────────────────────────────────
// src/repo/createRepo.ts
// Generic CRUD repo + outbox integration (soft-delete aware)
// Enhanced with TypeScript generics for type safety
// ─────────────────────────────────────────────────────────────────────────────

import { getDb } from "./dbrepo";
import {
  withBaseCreateFields,
  withBaseUpdateFields,
} from "../../utilis/baseEntity";
import { enqueueOutbox } from "../../sync/outbox";
import { BaseEntity, PaginatedResponse, RepositoryOptions } from "../../types";
import { IRepository } from "../../types/services";
import { productionLogger } from "../../services/productionLoggingService";

// Repository configuration interface
export interface RepoConfig {
  storeName: string;
  entityName: string;
  skipOutbox?: boolean; // Skip automatic outbox entries for this entity
}

// User context for operations
export interface UserContext {
  userId?: string;
}

// Generic repository factory function
export function createRepo<T extends BaseEntity>(
  config: RepoConfig
): IRepository<T> {
  const { storeName, entityName, skipOutbox = false } = config;

  // Create operation with proper typing
  async function create(
    data: Omit<T, "Id" | "CreatedDateTime">,
    options: UserContext = {}
  ): Promise<T> {
    const { userId } = options;
    const db = await getDb();
    const doc = withBaseCreateFields(data, userId) as T;
    
    // Configure transaction stores based on skipOutbox setting
    const stores = skipOutbox ? [storeName] : [storeName, "outbox"];
    const tx = db.transaction(stores, "readwrite");

    await tx.objectStore(storeName).put(doc);
    
    // Only add to outbox if not skipping
    if (!skipOutbox) {
      await enqueueOutbox({
        entity: entityName,
        entityId: doc.Id,
        op: "upsert",
        payload: doc,
      });
    }
    
    await tx.done;
    return doc;
  }

  // Get by ID with proper typing
  async function getById(id: string): Promise<T | null> {
    if (!id) return null;

    const db = await getDb();
    const result = await db.transaction(storeName).store.get(id);
    return result || null;
  }

  // List all records with filtering
  async function listAll(options: RepositoryOptions = {}): Promise<T[]> {
    const { includeDeactivated = false } = options;
    const db = await getDb();
    const tx = db.transaction(storeName);
    const store = tx.store;

    let cursor = await store.openCursor();
    const results: T[] = [];

    while (cursor) {
      const record = cursor.value as T;

      // Apply deactivation filter
      if (includeDeactivated || !record.Deactivated) {
        results.push(record);
      }

      cursor = await cursor.continue();
    }

    return results;
  }

  // Paginated list with enhanced filtering and performance optimization
  async function listPaged(
    options: RepositoryOptions = {}
  ): Promise<PaginatedResponse<T>> {
    const {
      page = 1,
      pageSize = 10,
      search = "",
      includeDeactivated = false,
    } = options;

    const db = await getDb();
    const tx = db.transaction(storeName);
    const store = tx.store;

    const skipCount = (page - 1) * pageSize;
    let skipped = 0;
    let taken = 0;
    let totalCount = 0;
    const results: T[] = [];

    try {
      let cursor;
      let usingIndex = false;

      if (includeDeactivated) {
        // Include all records, no index needed
        cursor = await store.openCursor(undefined, "prev");
      } else {
        // Use compound index to get only active records (Deactivated = false)
        try {
          const index = store.index("byActiveAndCreated");
          // Create a range for all records where Deactivated = false
          // Use only() for a single value match instead of bound() which requires proper key pairs
          const keyRange = IDBKeyRange.only([false]);
          cursor = await index.openCursor(keyRange, "prev"); // Most recent first
          usingIndex = true;
        } catch (indexError) {
          console.warn(
            "Index not available, falling back to manual filtering:",
            indexError
          );
          cursor = await store.openCursor(undefined, "prev");
        }
      }

      while (cursor) {
        const record = cursor.value as T;

        // Apply deactivation filter if not using index
        if (!usingIndex && !includeDeactivated && record.Deactivated) {
          cursor = await cursor.continue();
          continue;
        }

        // Apply search filter if provided
        if (search && !matchesSearch(record, search)) {
          cursor = await cursor.continue();
          continue;
        }

        totalCount++;

        // Skip records for pagination
        if (skipped < skipCount) {
          skipped++;
        } else if (taken < pageSize) {
          results.push(record);
          taken++;
        } else {
          // We have enough for this page, but continue counting for total
          // For performance, we'll estimate total based on current position
          break;
        }

        cursor = await cursor.continue();
      }

      // If we didn't get all records, we need to continue counting for accurate total
      if (taken === pageSize && cursor) {
        // Continue counting remaining records to get accurate total count
        // This is essential for correct pagination when jumping to specific pages
        while (cursor) {
          const record = cursor.value as T;

          // Apply same filters as above to ensure consistent counting
          if (!usingIndex && !includeDeactivated && record.Deactivated) {
            cursor = await cursor.continue();
            continue;
          }

          if (search && !matchesSearch(record, search)) {
            cursor = await cursor.continue();
            continue;
          }

          totalCount++;
          cursor = await cursor.continue();
        }
      }
    } catch (error) {
      console.error(`Error in listPaged for ${storeName}:`, error);
      throw new Error(`Failed to retrieve paginated ${entityName} list`);
    }

    // Log search results for debugging
    if (search && search.trim() !== '') {
    
      // Log search operation to production logger for analysis
      productionLogger.info('Local search performed', {
        context: 'local_search',
        entityType: entityName,
        searchTerm: search,
        totalMatches: totalCount,
        pageResults: results.length,
        page: page + 1,
        pageSize,
        searchWords: search.toLowerCase().trim().split(/\s+/).filter(word => word.length > 0)
      });
    }

    const totalPages = Math.ceil(totalCount / pageSize);

    return {
      data: results,
      page,
      pageSize,
      total: totalCount,
      totalPages,
      hasNextPage: page < totalPages,
      hasPreviousPage: page > 1,
    };
  }
 // Update operation with proper typing
  async function update(
    id: string,
    data: Partial<T>,
    options: UserContext = {}
  ): Promise<T> {
    if (!id) {
      throw new Error("ID is required for update operation");
    }

    const { userId } = options;
    const db = await getDb();
    
    // Configure transaction stores based on skipOutbox setting
    const stores = skipOutbox ? [storeName] : [storeName, "outbox"];
    const tx = db.transaction(stores, "readwrite");
    const store = tx.objectStore(storeName);

    // Get existing record
    const existing = await store.get(id);
    if (!existing) {
      throw new Error(`${entityName} with ID ${id} not found`);
    }

    // Properly merge existing data with updates, ensuring ID is preserved
    const mergedData = {
      ...existing,  // Start with existing data
      ...data,      // Apply updates
      Id: id        // Ensure ID is always preserved
    };

    // Add update metadata including ModifiedDateTime
    const updated = withBaseUpdateFields(mergedData, userId) as T;

    await store.put(updated);

    // Only add to outbox if not skipping
    if (!skipOutbox) {
      await enqueueOutbox({
        entity: entityName,
        entityId: id,
        op: "upsert",
        payload: updated,
      });
    }
    
    await tx.done;

    return updated;
  }
  // Soft delete (mark as deactivated)
  async function softDelete(
    id: string,
    options: UserContext = {}
  ): Promise<boolean> {
    if (!id) return false;

    const { userId } = options;

    try {
      const baseUpdateFields = withBaseUpdateFields(
        { Deactivated: true, DeactivatedAt: new Date().toISOString() },
        userId
      );
      await update(
        id,
        {
          Deactivated: true,
          DeactivatedAt: new Date().toISOString(),
          ...baseUpdateFields,
        } as unknown as Partial<T>,
        options
      );

      return true;
    } catch (error) {
      console.error(`Error soft-deleting ${entityName}:`, error);
      return false;
    }
  }

  // Hard delete (permanent removal)
  async function deleteRecord(id: string): Promise<boolean> {
    if (!id) return false;

    const db = await getDb();
    
    // Configure transaction stores based on skipOutbox setting
    const stores = skipOutbox ? [storeName] : [storeName, "outbox"];
    const tx = db.transaction(stores, "readwrite");

    try {
      await tx.objectStore(storeName).delete(id);
      
      // Only add to outbox if not skipping
      if (!skipOutbox) {
        await enqueueOutbox({
          entity: entityName,
          entityId: id,
          op: "delete",
          payload: null,
        });
      }
      
      await tx.done;
      return true;
    } catch (error) {
      console.error(`Error deleting ${entityName}:`, error);
      return false;
    }
  }

  // Search operation
  async function search(
    query: string,
    options: RepositoryOptions = {}
  ): Promise<T[]> {
    if (!query || query.trim().length < 2) {
      return [];
    }

    const allRecords = await listAll(options);
    return allRecords.filter((record) => matchesSearch(record, query));
  }

  // Count records
  async function count(
    options: { includeDeactivated?: boolean } = {}
  ): Promise<number> {
    const { includeDeactivated = false } = options;
    const db = await getDb();
    const store = db.transaction(storeName).store;

    if (includeDeactivated) {
      return await store.count();
    }

    // Count only active records
    let cursor = await store.openCursor();
    let activeCount = 0;

    while (cursor) {
      const record = cursor.value as T;
      if (!record.Deactivated) {
        activeCount++;
      }
      cursor = await cursor.continue();
    }

    return activeCount;
  }

  // Check if record exists
  async function exists(id: string): Promise<boolean> {
    if (!id) return false;
    const record = await getById(id);
    return !!record;
  }

  // Batch create
  async function createMany(
    items: Array<Omit<T, "Id" | "CreatedDateTime">>,
    options: UserContext = {}
  ): Promise<T[]> {
    const db = await getDb();
    const tx = db.transaction([storeName, "outbox"], "readwrite");
    const results: T[] = [];

    try {
      for (const item of items) {
        const doc = withBaseCreateFields(item, options.userId) as T;
        await tx.objectStore(storeName).put(doc);
        await enqueueOutbox({
          entity: entityName,
          entityId: doc.Id,
          op: "upsert",
          payload: doc,
        });
        results.push(doc);
      }
      await tx.done;
    } catch (error) {
      console.error(`Error in batch create for ${entityName}:`, error);
      throw new Error(`Failed to create multiple ${entityName} records`);
    }

    return results;
  }

  // Batch update
  async function updateMany(
    updates: Array<{ id: string; data: Partial<T> }>,
    options: UserContext = {}
  ): Promise<T[]> {
    const results: T[] = [];

    for (const { id, data } of updates) {
      try {
        const updated = await update(id, data, options);
        results.push(updated);
      } catch (error) {
        console.error(`Failed to update ${entityName} ${id}:`, error);
        // Continue with other updates
      }
    }

    return results;
  }

  // Batch delete
  async function deleteMany(ids: string[]): Promise<boolean> {
    let allSuccessful = true;

    for (const id of ids) {
      const success = await deleteRecord(id);
      if (!success) {
        allSuccessful = false;
      }
    }

    return allSuccessful;
  }

  // Create or update (upsert) operation
  async function createOrUpdate(
    id: string,
    data: Partial<T>,
    options: UserContext = {}
  ): Promise<T> {
    const existing = await getById(id);

    if (existing) {
      return await update(id, data, options);
    } else {
      // For create, we need to ensure required fields are present but exclude Id from create data
      const { Id, ...createFields } = data as any;
      const createData = {
        ...createFields,
      } as Omit<T, "Id" | "CreatedDateTime">;

      const created = await create(createData, options);

      // If we have a specific ID we wanted to use, update with it
      if (id !== created.Id) {
        return await update(created.Id, { Id: id } as Partial<T>, options);
      }

      return created;
    }
  }

  // Helper function for search matching - Enhanced for better patient search
  function matchesSearch(record: T, searchTerm: string): boolean {
    if (!searchTerm || searchTerm.trim() === '') return true;
    
    // Normalize search term: lowercase and split by spaces
    const searchWords = searchTerm.toLowerCase().trim().split(/\s+/).filter(word => word.length > 0);
    
    // If no valid search words, return true
    if (searchWords.length === 0) return true;

    // Define searchable fields for different entity types
    const getSearchableValues = (obj: any): string[] => {
      const values: string[] = [];
      
      // For Patient entities, focus on relevant fields
      if (obj && typeof obj === 'object') {
        // Patient-specific fields
        if (obj.FirstName) values.push(String(obj.FirstName));
        if (obj.LastName) values.push(String(obj.LastName));
        if (obj.Email) values.push(String(obj.Email));
        if (obj.PhoneNumber) values.push(String(obj.PhoneNumber));
        if (obj.Title) values.push(String(obj.Title));
        if (obj.NHSNumber) values.push(String(obj.NHSNumber));
        
        // Combine first and last name for full name search
        if (obj.FirstName && obj.LastName) {
          values.push(`${obj.FirstName} ${obj.LastName}`);
        }
        
        // Add any additional string fields that might be relevant for search
        for (const [key, value] of Object.entries(obj)) {
          if (typeof value === 'string' && value && 
              !['Id', 'CreatedDateTime', 'ModifiedDateTime', 'LastSyncedAt'].includes(key)) {
            values.push(value);
          }
        }
      }
      
      return values.map(v => String(v).toLowerCase());
    };

    const searchableValues = getSearchableValues(record);
    
    // Check if ALL search words are found in the searchable values
    const matches = searchWords.every(searchWord => {
      return searchableValues.some(value => value.includes(searchWord));
    });

    // Debug logging for search behavior (only in development)
    if (process.env.NODE_ENV === 'development' && searchWords.length > 0) {
      const patientInfo = (record as any).FirstName && (record as any).LastName 
        ? `${(record as any).FirstName} ${(record as any).LastName}` 
        : (record as any).Id || 'Unknown';
      
      if (!matches) {
        console.debug(`🔍 Search miss for "${searchTerm}" on ${patientInfo}:`, {
          searchWords,
          searchableValues,
          recordType: typeof record
        });
      }
    }
    
    return matches;
  }

  // Return the repository interface implementation
  return {
    create,
    getById,
    update,
    delete: deleteRecord,
    softDelete,
    listAll,
    listPaged,
    search,
    count,
    exists,
    createMany,
    updateMany,
    deleteMany,
    createOrUpdate,
  };
}

// Export type for convenience
export type Repository<T extends BaseEntity> = IRepository<T>;
